import React from 'react';
import { Loader } from '@getro/rombo';
import { Box, Flex, Text, Image } from 'rebass/styled-components';
import { Redirect } from 'react-router-dom';

import { MainLayoutHeader } from '../../components/atoms/mainLayoutHeader';
import MainLayout from '../../components/layout/Main';
import usePageHook from '../../hooks/usePageHook';
import { Toolbar } from '../../components/molecules/agGrid/components/toolbar';
import { MovementsTableView } from './movementsTableView';
import { MovementsSidebar } from './movementsFilters/movementsSidebar';
import { useMovementsPage } from './hooks/useMovementsPage';
import SearchNoResultsIcon from '../../assets/icon-effortless-search-no-results.svg';

export const Movements = () => {
  usePageHook('Movements');

  const {
    data,
    isFetching,
    filters,
    setFilters,
    filtersCount,
    showFilters,
    setShowFilters,
    columns,
    setColumns,
    showMainSkeleton,
    showRenderSkeleton,
    hasActiveFilters,
    hasMore,
    onLoadMore,
    isLoadingMore,
    tableRef,
    initialSortBy,
    currentSort,
    movementsSortOptions,
    toolbarOptionChange,
    onSort,
    activeNetwork,
    defaultColumns,
    page,
  } = useMovementsPage();

  if (!activeNetwork?.features?.includes('movements_page')) {
    return <Redirect to={`/networks/${activeNetwork?.slug}`} />;
  }

  return (
    <MainLayout containerProps={{ pb: 0 }}>
      <MainLayoutHeader title="Movements" />
      <Flex mx="auto" width="100%" flexDirection="column" sx={{ height: 'calc(100vh - 56px)', overflow: 'hidden' }}>
        <Box px="16px" flex="0 0 auto">
          <Box pb="16px" mt="8px">
            <Text fontSize="14px" color="text.main">
              Track when contacts join, leave, or get promoted at your portfolio companies.
            </Text>
          </Box>
        </Box>
        <Box flex="1" sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {showMainSkeleton && (
            <Flex flex="1" alignItems="center" justifyContent="center">
              <Loader />
            </Flex>
          )}
          {!showMainSkeleton && !showRenderSkeleton && data?.items?.length === 0 && !hasActiveFilters && (
            <Flex mt="40px" flexDirection="column" alignItems="center" maxWidth="520px" mx="auto" textAlign="center">
              <Image src={SearchNoResultsIcon} width="64px" maxWidth="64px" />
              <Text mt="16px" fontWeight="semibold">
                No movements yet
              </Text>
              <Text mt="8px" color="text.subtle">
                Movements will appear here as contacts join, leave, or get promoted at tracked companies.
              </Text>
            </Flex>
          )}
          {!showMainSkeleton && (data?.items?.length > 0 || hasActiveFilters || showRenderSkeleton) && (
            <>
              <Toolbar
                view="table"
                toolbarOptionChange={toolbarOptionChange}
                selectedColumns={columns || defaultColumns}
                type="default"
                page="movements"
                filtersVisible={showFilters}
                filtersCount={filtersCount}
                tableRef={tableRef}
                currentSort={currentSort}
                sortOptions={movementsSortOptions}
                showLayout={false}
              />
              <Flex flex="1" sx={{ position: 'relative', bg: 'neutral.0', overflow: 'hidden' }}>
                <MovementsSidebar
                  toggleFilters={() => setShowFilters(!showFilters)}
                  filters={filters}
                  setFilters={setFilters}
                  filtersCount={filtersCount}
                  showFilters={showFilters}
                />
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    minWidth: ['100vw', '100vw', 0],
                    transition: 'margin-left 0.3s ease-in-out',
                    marginLeft: showFilters ? '0px' : '-268px',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {(data?.items?.length > 0 || showRenderSkeleton) && (
                    <Box flex="1" sx={{ overflow: 'hidden' }}>
                      <MovementsTableView
                        ref={tableRef}
                        data={data?.items || []}
                        activeNetwork={activeNetwork}
                        columns={columns || defaultColumns}
                        setColumns={setColumns}
                        onLoadMore={onLoadMore}
                        hasMore={hasMore}
                        isLoadingMore={isLoadingMore}
                        onSort={onSort}
                        initialSortBy={initialSortBy}
                        showSkeleton={showRenderSkeleton}
                      />
                    </Box>
                  )}
                  {!showRenderSkeleton && !isFetching && data?.items?.length === 0 && hasActiveFilters && (
                    <Flex
                      mt="40px"
                      flexDirection="column"
                      alignItems="center"
                      maxWidth="520px"
                      mx="auto"
                      textAlign="center"
                    >
                      <Image src={SearchNoResultsIcon} width="64px" maxWidth="64px" />
                      <Text mt="16px" fontWeight="semibold">
                        No results
                      </Text>
                      <Text mt="8px" color="text.subtle">
                        Try adjusting your filters to find what you&apos;re looking for.
                      </Text>
                    </Flex>
                  )}
                </Box>
              </Flex>
            </>
          )}
          {!showMainSkeleton && !showRenderSkeleton && isFetching && page > 1 && (
            <Flex height="60px" py="16px" flexDirection="column" alignItems="center" maxWidth="480px" mx="auto">
              <Box>
                <Loader />
              </Box>
            </Flex>
          )}
        </Box>
      </Flex>
    </MainLayout>
  );
};
